<template>
    <div class="formation registrable-controller-index">
        <b-button
            v-if="registrableControllersList.registrableControllersDropdownList.length <= 0"
            variant="primary"
            class="d-flex justify-content-cener align-items-center"
            @click="openCreateRegistrableControllersForm"
        >
            <vector
                data-name="add-user"
                data-width="15px"
                data-height="12px"
                data-test="add-a-new-registrable-controller-btn"
                class="mr-.5 d-flex justify-content-center align-items-center"
            />
            Add a New registrable controller
        </b-button>

        <b-dropdown
            v-else
            id="dropdown-1"
            class="mt-.3"
            variant="primary"
            data-test="addNewRegistrableControllerDropDown"
        >
            <template #button-content>
                <div
                    class="
                        d-inline-flex
                        justify-content-center
                        align-items-center
                        text-wrap
                    "
                >
                    <vector
                        data-name="add-user"
                        data-width="15px"
                        data-height="12px"
                        class="
                            mr-.5
                            d-flex
                            justify-content-center
                            align-items-center
                        "
                    />
                    Add another registrable controller
                </div>
            </template>

            <b-dropdown-item
                style="font-size: 1.2em"
                variant="primary"
                data-test="add-a-new-registrable-controller-manually"
                @click="openCreateRegistrableControllersForm"
            >
                Add a new registrable controller manually
            </b-dropdown-item>

            <div v-if="registrableControllersList.registrableControllersDropdownList && registrableControllersList.registrableControllersDropdownList.length >= 1">
                <div class="dropdown-divider"></div>

                <b-dropdown-text class="text-ternary">
                    <span> Select from the list </span>
                </b-dropdown-text>

                <b-dropdown-item
                    v-for="registrableController in registrableControllersList.registrableControllersDropdownList"
                    :key="registrableController.name + '_' + registrableController.email"
                    class="text-primary text-uppercase"
                    variant="primary"
                    style="font-size: 1.2em"
                    :data-test="`registrableController-${registrableController.email}`"
                    @click="$emit('createRegistrableControllerFromDropdown', registrableController)"
                >
                    {{ registrableController.name }}
                </b-dropdown-item>
            </div>

        </b-dropdown>

        <loader v-if="isFetchingData" data-type="MinimumTaskList" class="mt-1 custom-loading" />

        <div v-if="registrableControllersList.registrableControllers && ! isFetchingData">
            <div
            v-for="registrableController in registrableControllersList.registrableControllers"
            :key="registrableController.name"
            class="mt-2"
            >
                <div
                    data-test="list-box"
                    class="list-box mb-1 row align-items-center"
                    style="margin-left: 0.1em; margin-right: 0.1em"
                >
                    <div
                        class="
                            user
                            d-flex
                            align-items-center
                            col-lg-4
                            justify-content-lg-start justify-content-md-center
                            mb-md-0 mb-sm-1
                        "
                    >

                        <b-avatar
                            size="30"
                            :text="getAvatarInitial(registrableController.name)"
                            :variant="`light-primary`"
                        />

                        <h3 class="font-weight-bold p-0 m-0 ml-1 text-break">
                            {{ registrableController.name }}
                        </h3>
                    </div>

                    <p
                        class="
                            m-0
                            p-0
                            col-lg-5
                            text-right
                            align-items-center
                            text-sm-center
                            mb-md-0 mb-sm-1
                            text-lg-right
                        "
                    >
                        {{ registrableController.email }}
                    </p>

                    <div
                        data-test="actions"
                        class="
                            actions
                            mb-md-0
                            d-flex
                            justify-content-md-end
                            align-items-center
                            col-lg-3
                            justify-content-sm-center
                        "
                    >
                        <span
                        :class="['steps-status d-block mr-2', getCompletedStepsOf(registrableController) === getTotalSteps(registrableController) ? 'text-success' : 'text-danger']"
                        >
                            {{ getCompletedStepsOf(registrableController) }} / {{ getTotalSteps(registrableController) }}
                        </span>

                        <div
                            class="mr-2 cursor-pointer"
                            :data-test="`edit-${registrableController.name}`"
                            @click="$emit('edit', registrableController, 'edit')"
                        >
                            <feather-icon size="21" icon="EditIcon" />
                        </div>

                        <div class="cursor-pointer">
                            <vector
                                data-type="icon"
                                data-name="cancel"
                                :data-test="`delete-${registrableController.name}`"
                                @click.native="$emit('remove', registrableController)"
                            ></vector>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import {
    BDropdown,
    BDropdownItem,
    BDropdownText,
    BAvatar,
    BButton
} from "bootstrap-vue";
import { SHAREHOLDER } from "@/constants/config";

export default {
    components: {
        BDropdown,
        BDropdownItem,
        BDropdownText,
        BAvatar,
        BButton
    },

    props: {
        registrableControllersList: {
            type: Object,
            required: true,
        },

        registrableControllerBluePrint: {
            type: Object,
            required: true,
        },

        isFetchingData: {
            type: Boolean,
            required: true
        }
    },

    data() {
        return {
            // ignore _id, dateOfExpiry and only one of nationality or country will present
            bluePrintLength: Object.keys(this.registrableControllerBluePrint).length - (1 + 1 + 1),
        };
    },

    methods: {
        openCreateRegistrableControllersForm(registrableController = this.registrableControllerBluePrint) {
            this.$emit("createRegistrableController", registrableController);
        },

        getTotalSteps(registrableController) { 
            if(registrableController.shareholderType.text === SHAREHOLDER.TYPE.INDIVIDUAL)
                return this.bluePrintLength - (10);

            return this.bluePrintLength - (7); 
        },

        getCompletedStepsOf(registrableController) {
            if (registrableController.shareholderType.text === SHAREHOLDER.TYPE.INDIVIDUAL)
            {
                // ignore _id, only one of nationality or country will present
                return Object.keys(this.registrableControllerBluePrint).filter(
                    key => !!registrableController[key] && !key.includes('alias') && !key.includes('sent_on') && !key.includes('received_on') && !key.includes('cessation_date') && !key.includes('remark') && !key.includes('legalForm') && !key.includes('jurisdiction') && !key.includes('statue') && !key.includes('registrar') && !key.includes('country') && !key.includes('uen') && !key.includes('dateOfExpiry')
                ).length - 1;
            }
            
            // ignore _id, only one of nationality or country will present
            return Object.keys(this.registrableControllerBluePrint).filter(
                key => !!registrableController[key] && !key.includes('alias') && !key.includes('sent_on') && !key.includes('received_on') && !key.includes('cessation_date') && !key.includes('remark') && !key.includes('NRIC') && !key.includes('idType') && !key.includes('nationality') && !key.includes('dateOfExpiry')
            ).length - 1;
        },
        
    },
};
</script>
